import React, { useState, useEffect } from 'react';
import { 
  Box, 
  SimpleGrid, 
  Flex, 
  Text, 
  Heading, 
  Spinner, 
  List, 
  ListItem,
  Divider,
  Icon,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody
} from '@chakra-ui/react';
import { 
  FaUsers, 
  FaStar, 
  FaSearch,
  FaChartLine,
  FaMapMarkerAlt
} from 'react-icons/fa';
import AdminService from '../../services/AdminService';

const StatCard = ({ title, value, icon, color }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const iconBgColor = useColorModeValue(`${color}.100`, `${color}.900`);
  
  return (
    <Card shadow="md" bg={bgColor} height="100%">
      <CardBody>
        <Flex align="center" mb={2}>
          <Flex
            bg={iconBgColor}
            borderRadius="full"
            p={2}
            mr={3}
            justify="center"
            align="center"
          >
            <Icon as={icon} color={`${color}.500`} boxSize={5} />
          </Flex>
          <Text fontWeight="medium" fontSize="lg">{title}</Text>
        </Flex>
        <Text fontSize="3xl" fontWeight="bold">
          {value}
        </Text>
      </CardBody>
    </Card>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bgColor = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const data = await AdminService.getDashboardStats();
        setStats(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard statistics');
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  if (loading) {
    return (
      <Flex justify="center" p={4}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        Dashboard Overview
      </Heading>
      
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <StatCard 
          title="Total Users" 
          value={stats?.total_users || 0} 
          icon={FaUsers}
          color="blue"
        />
        <StatCard 
          title="Premium Users" 
          value={stats?.premium_users || 0} 
          icon={FaStar}
          color="purple"
        />
        <StatCard 
          title="Total Searches" 
          value={stats?.total_searches || 0} 
          icon={FaSearch}
          color="green"
        />
        <StatCard 
          title="Recent Searches (7d)" 
          value={stats?.recent_searches || 0} 
          icon={FaChartLine}
          color="teal"
        />
      </SimpleGrid>
      
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Card shadow="md" bg={bgColor}>
          <CardHeader>
            <Heading size="sm">Most Active Users</Heading>
          </CardHeader>
          <CardBody>
            <List spacing={2}>
              {stats?.most_active_users?.length > 0 ? (
                stats.most_active_users.map((user, index) => (
                  <React.Fragment key={user.id}>
                    <ListItem>
                      <Box>
                        <Text fontWeight="medium">
                          {`${user.first_name || ''} ${user.last_name || ''} (${user.email})`}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {`${user.search_count} searches`}
                        </Text>
                      </Box>
                    </ListItem>
                    {index < stats.most_active_users.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <ListItem>
                  <Text>No data available</Text>
                </ListItem>
              )}
            </List>
          </CardBody>
        </Card>
        
        <Card shadow="md" bg={bgColor}>
          <CardHeader>
            <Heading size="sm">Most Searched Postcodes</Heading>
          </CardHeader>
          <CardBody>
            <List spacing={2}>
              {stats?.most_searched_postcodes?.length > 0 ? (
                stats.most_searched_postcodes.map((item, index) => (
                  <React.Fragment key={item.postcode}>
                    <ListItem>
                      <Flex align="center">
                        <Icon as={FaMapMarkerAlt} color="blue.500" mr={2} />
                        <Box>
                          <Text fontWeight="medium">{item.postcode}</Text>
                          <Text fontSize="sm" color="gray.500">
                            {`${item.search_count} searches`}
                          </Text>
                        </Box>
                      </Flex>
                    </ListItem>
                    {index < stats.most_searched_postcodes.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <ListItem>
                  <Text>No data available</Text>
                </ListItem>
              )}
            </List>
          </CardBody>
        </Card>
      </SimpleGrid>
    </Box>
  );
};

export default Dashboard;
